import { useEffect, useMemo, useState } from 'react';
import { Form } from '@bedrock/components';
import { useDebounceFn } from 'ahooks';
import { testBridgeRegistry, isTestBridgeEnabled } from '../../test-bridge';

export const useSForm = function <T = Record<string, any>>(props?: {
  initialValues?: T;
  formId?: string;
  /** 校验失败后滚动到 error field 的实现（缺省走 form.scrollToField）。
      吸顶布局场景（header/tabs sticky）传入，手动计算偏移避免 error field 被吸顶元素遮挡 */
  scrollToErrorField?: (
    name: any,
    options?: { behavior?: ScrollBehavior }
  ) => void;
}) {
  const { initialValues, formId, scrollToErrorField } = props || {};
  const [formBase] = Form.useForm();
  // @ts-ignore
  const [formData, setFormData] = useState<T>(initialValues || {});

  useEffect(() => {
    if (initialValues) {
      formBase.setFieldsValue?.(initialValues);
    } else {
      setFormData(formBase.getFieldsValue());
    }
  }, []);

  // 更新formData，用于注入到表单中，用于表单内部联动
  const { run: updateFormData } = useDebounceFn(
    () => {
      setFormData({ ...formBase.getFieldsValue() });
    },
    {
      wait: 200,
      leading: false,
      trailing: true,
    }
  );

  const form: typeof formBase = useMemo(() => {
    return {
      ...formBase,
      validateFields: (...args) => {
        const proms = formBase.validateFields(...args);
        proms.catch((err) => {
          if (err.errorFields.length) {
            if (scrollToErrorField) {
              scrollToErrorField(err.errorFields[0].name, {
                behavior: 'smooth',
              });
            } else {
              form.scrollToField(err.errorFields[0].name, {
                behavior: 'smooth',
              });
            }
          }
        });
        return proms;
      },
      setFieldsValue: (values) => {
        formBase.setFieldsValue(values);
        updateFormData();
      },
      setFieldValue: (name, value) => {
        formBase.setFieldValue(name, value);
        updateFormData();
      },
      resetFields: () => {
        formBase.resetFields();
        updateFormData();
      },
    };
  }, [formBase, scrollToErrorField]);

  // 挂载点 A（采集双挂，覆盖 useSForm + 直接 SFormItem 场景；元信息降级仅状态类）
  useEffect(() => {
    if (!formId || !isTestBridgeEnabled()) return;
    testBridgeRegistry.registerForm(formId, {
      getInstance: () => form,
      getFieldMetas: () => [],
    });
    return () => testBridgeRegistry.unregisterForm(formId);
  }, [formId, form]);

  return {
    form,
    formData,
    updateFormData: updateFormData,
  };
};
